const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');

class Scratch3MadBotTrainableAI {
    constructor(runtime) {
        this.runtime = runtime;

        // Teachable Machine model
        this.model = null;
        this.modelBaseURL = '';

        // MadBot ML Lab models
        this.madbotModels = {
            image: null,
            draw: null,
            text: null,
            number: null
        };

        this.projectName = '';
        this.modelSource = 'none'; // none | teachableMachine | madbotMLLab
        this.activeModelType = '';

        this.labels = [];
        this.predictions = [];

        this.rawLabel = '';
        this.rawConfidence = 0;

        this.bestLabel = '';
        this.bestConfidence = 0;

        this.predictedNumberValue = 0;
        this.status = 'Model not loaded';

        this.source = 'camera';
        this.running = false;
        this.cameraOn = false;
        this.isPredicting = false;
        this.predictionTimer = null;

        this.fallbackStream = null;
        this.fallbackVideo = null;

        this.tempCanvas = null;
        this.modelInputCanvas = null;
        this.drawInputCanvas = null;
        this.imageInputCanvas = null;

        this.cameraWidth = 480;
        this.cameraHeight = 360;
        this.modelInputSize = 224;
        this.predictionIntervalMs = 250;

        // Prediction smoothing
        // This prevents fast flickering like B -> A -> B.
        this.predictionHistory = [];
        this.historySize = 5;
        this.requiredStableFrames = 3;
        this.requiredInitialFrames = 2;
        this.minStableProbability = 0.35;
        this.minSwitchMargin = 0.08;
        this.strongSwitchProbability = 0.9;
        this.strongSwitchMargin = 0.18;

        this._removeOldDebugPreview();
    }

    getInfo() {
        return {
            id: 'madbotTrainableAI',
            name: 'Trainable AI',
            color1: '#7C3AED',
            color2: '#6D28D9',
            color3: '#4C1D95',
            blocks: [
                {
                    opcode: 'loadModel',
                    blockType: BlockType.COMMAND,
                    text: 'load Teachable Machine model [URL]',
                    arguments: {
                        URL: {
                            type: ArgumentType.STRING,
                            defaultValue: 'https://teachablemachine.withgoogle.com/models/your-model-id/'
                        }
                    }
                },
                {
                    opcode: 'loadMadBotMLModelFile',
                    blockType: BlockType.COMMAND,
                    text: 'load MadBot ML Lab model file'
                },
                {
                    opcode: 'isModelLoaded',
                    blockType: BlockType.BOOLEAN,
                    text: 'model loaded?'
                },
                {
                    opcode: 'turnCamera',
                    blockType: BlockType.COMMAND,
                    text: 'turn trainable AI camera [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'ON_OFF_MENU',
                            defaultValue: 'on'
                        }
                    }
                },
                {
                    opcode: 'startPredictionFrom',
                    blockType: BlockType.COMMAND,
                    text: 'start prediction from [SOURCE]',
                    arguments: {
                        SOURCE: {
                            type: ArgumentType.STRING,
                            menu: 'SOURCE_MENU',
                            defaultValue: 'camera'
                        }
                    }
                },
                {
                    opcode: 'stopPrediction',
                    blockType: BlockType.COMMAND,
                    text: 'stop prediction'
                },
                '---',
                {
                    opcode: 'predictText',
                    blockType: BlockType.COMMAND,
                    text: 'predict text [TEXT]',
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'I am happy today'
                        }
                    }
                },
                {
                    opcode: 'predictNumber',
                    blockType: BlockType.COMMAND,
                    text: 'predict number [VALUE]',
                    arguments: {
                        VALUE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 5
                        }
                    }
                },
                '---',
                {
                    opcode: 'predictedClass',
                    blockType: BlockType.REPORTER,
                    text: 'predicted class'
                },
                {
                    opcode: 'confidence',
                    blockType: BlockType.REPORTER,
                    text: 'prediction confidence'
                },
                {
                    opcode: 'predictedNumber',
                    blockType: BlockType.REPORTER,
                    text: 'predicted number'
                },
                {
                    opcode: 'isClassDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is class [CLASS] detected above [CONFIDENCE] % ?',
                    arguments: {
                        CLASS: {
                            type: ArgumentType.STRING,
                            menu: 'CLASS_MENU',
                            defaultValue: 'class 1'
                        },
                        CONFIDENCE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 80
                        }
                    }
                },
                {
                    opcode: 'confidenceOfClass',
                    blockType: BlockType.REPORTER,
                    text: 'confidence of class [CLASS]',
                    arguments: {
                        CLASS: {
                            type: ArgumentType.STRING,
                            menu: 'CLASS_MENU',
                            defaultValue: 'class 1'
                        }
                    }
                },
                {
                    opcode: 'whenClassDetected',
                    blockType: BlockType.HAT,
                    text: 'when class [CLASS] detected above [CONFIDENCE] %',
                    arguments: {
                        CLASS: {
                            type: ArgumentType.STRING,
                            menu: 'CLASS_MENU',
                            defaultValue: 'class 1'
                        },
                        CONFIDENCE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 80
                        }
                    }
                },
                '---',
                {
                    opcode: 'allClasses',
                    blockType: BlockType.REPORTER,
                    text: 'all model classes'
                },
                {
                    opcode: 'modelType',
                    blockType: BlockType.REPORTER,
                    text: 'model type'
                },
                {
                    opcode: 'modelStatus',
                    blockType: BlockType.REPORTER,
                    text: 'trainable AI status'
                }
            ],
            menus: {
                SOURCE_MENU: {
                    acceptReporters: false,
                    items: [
                        {
                            text: 'camera',
                            value: 'camera'
                        },
                        {
                            text: 'full stage',
                            value: 'stage'
                        },
                        {
                            text: 'current backdrop',
                            value: 'backdrop'
                        }
                    ]
                },
                ON_OFF_MENU: {
                    acceptReporters: false,
                    items: [
                        {
                            text: 'on',
                            value: 'on'
                        },
                        {
                            text: 'off',
                            value: 'off'
                        }
                    ]
                },
                CLASS_MENU: {
                    acceptReporters: true,
                    items: '_getClassMenu'
                }
            }
        };
    }

    _getClassMenu() {
        if (this.labels && this.labels.length > 0) {
            return this.labels.map(label => ({
                text: label,
                value: label
            }));
        }

        return [
            {
                text: 'load model first',
                value: 'load model first'
            }
        ];
    }

    async loadModel(args) {
        const url = this._normalizeModelURL(Cast.toString(args.URL));

        if (!url) {
            this.status = 'Please enter Teachable Machine model URL';
            return;
        }

        this.status = 'Loading Teachable Machine libraries...';

        try {
            await this._ensureLibraries();
        } catch (error) {
            this.status = `Library loading failed: ${error.message}`;
            console.error('[MadBot Trainable AI] Library loading failed:', error);
            return;
        }

        this.status = 'Loading Teachable Machine model...';

        const modelURL = `${url}model.json`;
        const metadataURL = `${url}metadata.json`;

        try {
            this.model = await window.tmImage.load(modelURL, metadataURL);

            if (typeof this.model.getClassLabels === 'function') {
                this.labels = this.model.getClassLabels();
            } else {
                this.labels = [];
            }

            this.modelBaseURL = url;
            this.modelSource = 'teachableMachine';
            this.projectName = 'Teachable Machine Model';
            this.activeModelType = 'image';

            this._clearMadBotModels();
            this._clearPredictionState();
            this._detectModelInputSize();

            this.status = `Teachable Machine model loaded: ${this.labels.join(', ')}`;
        } catch (error) {
            this.model = null;
            this.labels = [];
            this.modelSource = 'none';
            this.status = `Model loading failed: ${error.message}`;
            console.error('[MadBot Trainable AI] Model loading failed:', error);
        }
    }

    async loadMadBotMLModelFile() {
        try {
            const json = await this._pickJsonFile();
            this._loadMadBotMLLabFile(json);
            this.status = `MadBot ML Lab model loaded: ${this.modelType()}`;
        } catch (error) {
            this.status = error && error.message ? error.message : 'Could not load MadBot ML Lab model file';
            console.error('[MadBot Trainable AI] MadBot ML Lab loading failed:', error);
        }
    }

    isModelLoaded() {
        if (this.modelSource === 'teachableMachine' && this.model) return true;
        if (this.modelSource === 'madbotMLLab' && this._hasAnyMadBotModel()) return true;
        return false;
    }

    async turnCamera(args) {
        const state = Cast.toString(args.STATE).toLowerCase();

        if (state === 'on') {
            await this._enableStageCamera();
            this.cameraOn = true;
            this.status = 'Camera on';
        } else {
            this._disableStageCamera();
            this.cameraOn = false;
            this.status = 'Camera off';
        }
    }

    async startPredictionFrom(args) {
        if (!this._hasVisualModel()) {
            this.status = 'Load a Teachable Machine image model or MadBot image/draw model first';
            return;
        }

        const source = Cast.toString(args.SOURCE).toLowerCase();

        if (source === 'stage') {
            this.source = 'stage';
        } else if (source === 'backdrop') {
            this.source = 'backdrop';
        } else {
            this.source = 'camera';
        }

        if (this.source === 'camera') {
            await this._enableStageCamera();
            this.cameraOn = true;
        }

        this.running = true;
        this._resetStabilizer();
        this.status = `Prediction started from ${this.source}`;

        if (this.predictionTimer) {
            clearInterval(this.predictionTimer);
            this.predictionTimer = null;
        }

        await this._predictOnce();

        this.predictionTimer = setInterval(() => {
            this._predictOnce();
        }, this.predictionIntervalMs);
    }

    stopPrediction() {
        this.running = false;
        this.isPredicting = false;

        if (this.predictionTimer) {
            clearInterval(this.predictionTimer);
            this.predictionTimer = null;
        }

        this._resetStabilizer();
        this.status = 'Prediction stopped';
    }

    predictText(args) {
        const text = Cast.toString(args.TEXT || '').trim();

        if (!this.madbotModels.text) {
            this.status = 'Load a MadBot ML Lab text model first';
            return;
        }

        if (!text) {
            this.status = 'Type some text to predict';
            return;
        }

        const result = this._predictNaiveBayes(text, this.madbotModels.text.model);

        this._setPredictions(result.probabilities, {
            smooth: false,
            statusPrefix: 'Text'
        });

        this.activeModelType = 'text';
        this.status = `Text detected ${this.bestLabel}`;
    }

    predictNumber(args) {
        const x = Cast.toNumber(args.VALUE);

        if (!this.madbotModels.number) {
            this.status = 'Load a MadBot ML Lab number model first';
            return;
        }

        if (!Number.isFinite(x)) {
            this.status = 'Enter a valid number';
            return;
        }

        const model = this.madbotModels.number.model;
        this.predictedNumberValue = model.m * x + model.b;
        this.activeModelType = 'number';
        this.status = `Number predicted: ${this._round(this.predictedNumberValue, 3)}`;
    }

    predictedClass() {
        return this.bestLabel || '';
    }

    confidence() {
        return this._round1(this.bestConfidence);
    }

    predictedNumber() {
        return this._round(this.predictedNumberValue, 3);
    }

    isClassDetected(args) {
        const selectedClass = Cast.toString(args.CLASS);
        const threshold = Cast.toNumber(args.CONFIDENCE);

        return this._sameLabel(this.bestLabel, selectedClass) && this.bestConfidence >= threshold;
    }

    confidenceOfClass(args) {
        const selectedClass = Cast.toString(args.CLASS);

        const prediction = this.predictions.find(item =>
            this._sameLabel(item.className, selectedClass)
        );

        if (!prediction) {
            return 0;
        }

        return this._round1(prediction.probability * 100);
    }

    whenClassDetected(args) {
        return this.isClassDetected(args);
    }

    allClasses() {
        return this.labels.join(', ');
    }

    modelType() {
        if (this.modelSource === 'teachableMachine') {
            return 'teachable machine image';
        }

        const types = [];

        if (this.madbotModels.image) types.push('image');
        if (this.madbotModels.draw) types.push('draw');
        if (this.madbotModels.text) types.push('text');
        if (this.madbotModels.number) types.push('number');

        return types.length ? types.join(', ') : 'none';
    }

    modelStatus() {
        return this.status;
    }

    async _ensureLibraries() {
        if (!window.__madbotTrainableAIScripts) {
            window.__madbotTrainableAIScripts = {};
        }

        if (!window.tf) {
            await this._loadScript(
                'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@1.3.1/dist/tf.min.js',
                () => window.tf
            );
        }

        if (!window.tmImage) {
            await this._loadScript(
                'https://cdn.jsdelivr.net/npm/@teachablemachine/image@0.8.3/dist/teachablemachine-image.min.js',
                () => window.tmImage
            );
        }
    }

    _loadScript(src, checkReady) {
        if (checkReady()) {
            return Promise.resolve();
        }

        if (window.__madbotTrainableAIScripts[src]) {
            return window.__madbotTrainableAIScripts[src];
        }

        window.__madbotTrainableAIScripts[src] = new Promise((resolve, reject) => {
            const existingScript = document.querySelector(`script[src="${src}"]`);

            if (existingScript) {
                existingScript.addEventListener('load', () => resolve());
                existingScript.addEventListener('error', () => reject(new Error(`Failed to load script: ${src}`)));
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.crossOrigin = 'anonymous';

            script.onload = () => {
                if (checkReady()) {
                    resolve();
                } else {
                    reject(new Error(`Library loaded but not ready: ${src}`));
                }
            };

            script.onerror = () => {
                reject(new Error(`Failed to load script: ${src}`));
            };

            document.head.appendChild(script);
        });

        return window.__madbotTrainableAIScripts[src];
    }

    _normalizeModelURL(url) {
        let cleanURL = (url || '').trim();

        if (!cleanURL) {
            return '';
        }

        cleanURL = cleanURL.replace(/\\/g, '/');

        if (cleanURL.endsWith('model.json')) {
            cleanURL = cleanURL.replace(/model\.json$/, '');
        }

        if (cleanURL.endsWith('metadata.json')) {
            cleanURL = cleanURL.replace(/metadata\.json$/, '');
        }

        if (!cleanURL.endsWith('/')) {
            cleanURL += '/';
        }

        return cleanURL;
    }

    _detectModelInputSize() {
        this.modelInputSize = 224;

        try {
            if (
                this.model &&
                this.model.model &&
                this.model.model.inputs &&
                this.model.model.inputs[0] &&
                this.model.model.inputs[0].shape &&
                this.model.model.inputs[0].shape[1]
            ) {
                const detectedSize = this.model.model.inputs[0].shape[1];

                if (detectedSize && detectedSize > 0) {
                    this.modelInputSize = detectedSize;
                }
            }
        } catch (error) {
            this.modelInputSize = 224;
        }
    }

    async _enableStageCamera() {
        const videoDevice = this._getScratchVideoDevice();

        if (videoDevice && typeof videoDevice.enableVideo === 'function') {
            try {
                await videoDevice.enableVideo();

                if (typeof videoDevice.setPreviewGhost === 'function') {
                    videoDevice.setPreviewGhost(0);
                }

                this._requestStageRedraw();
                return;
            } catch (error) {
                console.warn('[MadBot Trainable AI] Scratch camera failed, using fallback camera:', error);
            }
        }

        await this._enableFallbackCamera();
    }

    _disableStageCamera() {
        const videoDevice = this._getScratchVideoDevice();

        if (videoDevice && typeof videoDevice.disableVideo === 'function') {
            try {
                videoDevice.disableVideo();
            } catch (error) {
                console.warn('[MadBot Trainable AI] Could not disable Scratch camera:', error);
            }
        }

        this._disableFallbackCamera();
        this._requestStageRedraw();
    }

    _getScratchVideoDevice() {
        if (
            this.runtime &&
            this.runtime.ioDevices &&
            this.runtime.ioDevices.video
        ) {
            return this.runtime.ioDevices.video;
        }

        return null;
    }

    async _enableFallbackCamera() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.status = 'Camera not supported in this browser';
            return;
        }

        if (!this.fallbackVideo) {
            this.fallbackVideo = document.createElement('video');
            this.fallbackVideo.setAttribute('playsinline', true);
            this.fallbackVideo.muted = true;
            this.fallbackVideo.autoplay = true;
            this.fallbackVideo.width = this.cameraWidth;
            this.fallbackVideo.height = this.cameraHeight;
            this.fallbackVideo.style.display = 'none';
            document.body.appendChild(this.fallbackVideo);
        }

        if (!this.fallbackStream) {
            this.fallbackStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: {
                        ideal: this.cameraWidth
                    },
                    height: {
                        ideal: this.cameraHeight
                    },
                    facingMode: 'user'
                },
                audio: false
            });
        }

        this.fallbackVideo.srcObject = this.fallbackStream;
        await this.fallbackVideo.play();
    }

    _disableFallbackCamera() {
        if (this.fallbackStream) {
            this.fallbackStream.getTracks().forEach(track => track.stop());
            this.fallbackStream = null;
        }

        if (this.fallbackVideo) {
            this.fallbackVideo.pause();
            this.fallbackVideo.srcObject = null;
        }
    }

    async _predictOnce() {
        if (!this.running || this.isPredicting) {
            return;
        }

        if (this.modelSource === 'teachableMachine' && !this.model) {
            return;
        }

        if (this.modelSource === 'madbotMLLab' && !this._hasVisualModel()) {
            return;
        }

        this.isPredicting = true;

        try {
            if (this.modelSource === 'teachableMachine') {
                await this._predictTeachableMachineOnce();
            } else if (this.modelSource === 'madbotMLLab') {
                await this._predictMadBotVisualOnce();
            }
        } catch (error) {
            this.status = `Prediction error: ${error.message}`;
            console.error('[MadBot Trainable AI] Prediction error:', error);
        } finally {
            this.isPredicting = false;
        }
    }

    async _predictTeachableMachineOnce() {
        let inputElement = null;

        if (this.source === 'camera') {
            inputElement = await this._getCameraFrameForPrediction();
        } else if (this.source === 'stage') {
            inputElement = await this._getStageFrameForPrediction();
        } else if (this.source === 'backdrop') {
            inputElement = await this._getBackdropFrameForPrediction();
        }

        if (!inputElement) {
            return;
        }

        const prediction = await this.model.predict(inputElement, false);

        this._setPredictions(prediction, {
            smooth: true,
            statusPrefix: 'Detected'
        });
    }

    async _predictMadBotVisualOnce() {
        const visualModelType = this._chooseMadBotVisualModelType();
        const visualModel = this.madbotModels[visualModelType];

        if (!visualModel) {
            this.status = 'No image/draw model loaded';
            return;
        }

        let rawCanvas = null;

        if (this.source === 'camera') {
            const frame = await this._getCameraFrameForPrediction();
            rawCanvas = this._sourceToCanvas(frame);
        } else if (this.source === 'stage') {
            if (visualModelType === 'draw') {
                rawCanvas = await this._captureStageWithoutSpritesCanvas();
            } else {
                rawCanvas = await this._captureStageSnapshotCanvas();
            }
        } else if (this.source === 'backdrop') {
            rawCanvas = await this._captureBackdropCanvas();
        }

        if (!rawCanvas) {
            return;
        }

        let vector = null;

        if (visualModelType === 'draw') {
            vector = this._drawCanvasToVector(rawCanvas, visualModel.inputSize || 28);
        } else {
            vector = this._imageCanvasToVector(rawCanvas, visualModel.inputSize || 32);
        }

        if (!vector) {
            this.status = 'No clear drawing/object found. Keeping last result.';
            return;
        }

        const result = this._predictKNN(vector, visualModel.classes, visualModel.k || 5);
        this.activeModelType = visualModelType;

        this._setPredictions(result.probabilities, {
            smooth: true,
            statusPrefix: 'Detected'
        });
    }

    async _getCameraFrameForPrediction() {
        const videoDevice = this._getScratchVideoDevice();

        if (videoDevice && typeof videoDevice.getFrame === 'function') {
            try {
                const frame = videoDevice.getFrame({
                    format: 'canvas',
                    dimensions: [this.modelInputSize, this.modelInputSize],
                    mirror: true,
                    cacheTimeout: 0
                });

                if (frame) {
                    return frame;
                }
            } catch (error) {
                console.warn('[MadBot Trainable AI] Could not get Scratch camera canvas frame:', error);
            }

            try {
                const imageData = videoDevice.getFrame({
                    format: 'image-data',
                    dimensions: [this.modelInputSize, this.modelInputSize],
                    mirror: true,
                    cacheTimeout: 0
                });

                if (imageData) {
                    return this._imageDataToCanvas(imageData);
                }
            } catch (error) {
                console.warn('[MadBot Trainable AI] Could not get Scratch camera image data:', error);
            }
        }

        if (this.fallbackVideo && this.fallbackVideo.readyState >= 2) {
            return this._videoToSquareCanvas(this.fallbackVideo);
        }

        this.status = 'Camera frame not available';
        return null;
    }

    async _getStageFrameForPrediction() {
        const snapshotCanvas = await this._captureStageSnapshotCanvas();

        if (!snapshotCanvas) {
            this.status = 'Stage capture not available';
            return null;
        }

        return this._makeSquareModelInput(snapshotCanvas);
    }

    async _getBackdropFrameForPrediction() {
        const rawCanvas = await this._captureBackdropCanvas();

        if (!rawCanvas) {
            return null;
        }

        return this._makeSquareModelInput(rawCanvas);
    }

    _captureStageSnapshotCanvas() {
        return new Promise(resolve => {
            if (!this.runtime || !this.runtime.renderer || !this.runtime.renderer.requestSnapshot) {
                this.status = 'Stage snapshot not available';
                resolve(null);
                return;
            }

            this._requestStageRedraw();

            setTimeout(() => {
                this.runtime.renderer.requestSnapshot(dataURI => {
                    if (!dataURI) {
                        this.status = 'Empty stage snapshot';
                        resolve(null);
                        return;
                    }

                    this._dataURIToCanvas(dataURI).then(canvas => {
                        if (!canvas) {
                            this.status = 'Could not read stage image';
                            resolve(null);
                            return;
                        }

                        resolve(canvas);
                    });
                });
            }, 100);
        });
    }

    async _captureStageWithoutSpritesCanvas() {
        if (!this.runtime || !this.runtime.targets) {
            return this._captureStageSnapshotCanvas();
        }

        const changedTargets = [];

        try {
            this.runtime.targets.forEach(target => {
                if (!target || target.isStage) return;

                const wasVisible = target.visible;

                if (wasVisible) {
                    changedTargets.push({
                        target,
                        visible: wasVisible
                    });

                    if (typeof target.setVisible === 'function') {
                        target.setVisible(false);
                    } else {
                        target.visible = false;
                    }
                }
            });

            this._requestStageRedraw();

            await new Promise(resolve => setTimeout(resolve, 80));

            const canvas = await this._captureStageSnapshotCanvas();

            return canvas;
        } catch (error) {
            console.warn('[MadBot Trainable AI] Could not capture stage without sprites:', error);
            return this._captureStageSnapshotCanvas();
        } finally {
            changedTargets.forEach(item => {
                if (!item.target) return;

                if (typeof item.target.setVisible === 'function') {
                    item.target.setVisible(item.visible);
                } else {
                    item.target.visible = item.visible;
                }
            });

            this._requestStageRedraw();
        }
    }

    async _captureBackdropCanvas() {
        try {
            if (!this.runtime || typeof this.runtime.getTargetForStage !== 'function') {
                this.status = 'Runtime stage not available';
                return null;
            }

            const stageTarget = this.runtime.getTargetForStage();

            if (!stageTarget || !stageTarget.sprite || !stageTarget.sprite.costumes) {
                this.status = 'Backdrop target not found';
                return null;
            }

            const costumeIndex = stageTarget.currentCostume || 0;
            const costume = stageTarget.sprite.costumes[costumeIndex];

            if (!costume || !costume.asset) {
                this.status = 'Backdrop image not found';
                return null;
            }

            let dataURI = '';

            if (typeof costume.asset.encodeDataURI === 'function') {
                dataURI = costume.asset.encodeDataURI();
            } else if (costume.asset.data) {
                const dataFormat = costume.asset.dataFormat || 'png';
                const mimeType = dataFormat === 'svg' ? 'image/svg+xml' : `image/${dataFormat}`;
                const base64 = this._arrayBufferToBase64(costume.asset.data);
                dataURI = `data:${mimeType};base64,${base64}`;
            }

            if (!dataURI) {
                this.status = 'Could not read backdrop image';
                return null;
            }

            const rawCanvas = await this._dataURIToCanvas(dataURI);

            if (!rawCanvas) {
                this.status = 'Could not convert backdrop image';
                return null;
            }

            return rawCanvas;
        } catch (error) {
            this.status = `Backdrop error: ${error.message}`;
            console.error('[MadBot Trainable AI] Backdrop prediction error:', error);
            return null;
        }
    }

    _dataURIToCanvas(dataURI) {
        return new Promise(resolve => {
            const img = new Image();

            img.onload = () => {
                const canvas = document.createElement('canvas');

                const width = img.naturalWidth || this.cameraWidth || 480;
                const height = img.naturalHeight || this.cameraHeight || 360;

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d', {
                    willReadFrequently: true
                });

                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);

                resolve(canvas);
            };

            img.onerror = () => {
                resolve(null);
            };

            img.src = dataURI;
        });
    }

    _makeSquareModelInput(sourceCanvas) {
        if (!sourceCanvas) {
            return null;
        }

        if (!this.modelInputCanvas) {
            this.modelInputCanvas = document.createElement('canvas');
        }

        const size = this.modelInputSize || 224;

        this.modelInputCanvas.width = size;
        this.modelInputCanvas.height = size;

        const ctx = this.modelInputCanvas.getContext('2d', {
            willReadFrequently: true
        });

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);

        const sourceWidth = sourceCanvas.width;
        const sourceHeight = sourceCanvas.height;

        const scale = Math.max(size / sourceWidth, size / sourceHeight);
        const drawWidth = Math.round(sourceWidth * scale);
        const drawHeight = Math.round(sourceHeight * scale);
        const drawX = Math.round((size - drawWidth) / 2);
        const drawY = Math.round((size - drawHeight) / 2);

        ctx.drawImage(
            sourceCanvas,
            0,
            0,
            sourceWidth,
            sourceHeight,
            drawX,
            drawY,
            drawWidth,
            drawHeight
        );

        return this.modelInputCanvas;
    }

    _videoToSquareCanvas(video) {
        if (!video || video.readyState < 2) {
            return null;
        }

        if (!this.modelInputCanvas) {
            this.modelInputCanvas = document.createElement('canvas');
        }

        const size = this.modelInputSize || 224;

        this.modelInputCanvas.width = size;
        this.modelInputCanvas.height = size;

        const ctx = this.modelInputCanvas.getContext('2d', {
            willReadFrequently: true
        });

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);

        const sourceWidth = video.videoWidth || video.width || this.cameraWidth;
        const sourceHeight = video.videoHeight || video.height || this.cameraHeight;

        const cropSize = Math.min(sourceWidth, sourceHeight);
        const cropX = Math.floor((sourceWidth - cropSize) / 2);
        const cropY = Math.floor((sourceHeight - cropSize) / 2);

        ctx.drawImage(
            video,
            cropX,
            cropY,
            cropSize,
            cropSize,
            0,
            0,
            size,
            size
        );

        return this.modelInputCanvas;
    }

    _sourceToCanvas(source) {
        if (!source) return null;

        if (source instanceof HTMLCanvasElement) return source;

        if (source instanceof ImageData) {
            return this._imageDataToCanvas(source);
        }

        const width = source.videoWidth || source.naturalWidth || source.width || this.cameraWidth;
        const height = source.videoHeight || source.naturalHeight || source.height || this.cameraHeight;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d', {
            willReadFrequently: true
        });

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(source, 0, 0, width, height);

        return canvas;
    }

    _imageDataToCanvas(imageData) {
        if (!this.tempCanvas) {
            this.tempCanvas = document.createElement('canvas');
        }

        this.tempCanvas.width = imageData.width;
        this.tempCanvas.height = imageData.height;

        const ctx = this.tempCanvas.getContext('2d', {
            willReadFrequently: true
        });

        ctx.putImageData(imageData, 0, 0);

        return this.tempCanvas;
    }

    _requestStageRedraw() {
        if (!this.runtime || !this.runtime.renderer) {
            return;
        }

        try {
            if (typeof this.runtime.renderer.draw === 'function') {
                this.runtime.renderer.draw();
            }

            if (typeof this.runtime.requestRedraw === 'function') {
                this.runtime.requestRedraw();
            }
        } catch (error) {
            console.warn('[MadBot Trainable AI] Stage redraw request failed:', error);
        }
    }

    _setPredictions(prediction, options = {}) {
        const list = (prediction || []).map(item => ({
            className: item.className || item.label || item.name || '',
            probability: Number(item.probability !== undefined ? item.probability : item.confidence) || 0
        })).filter(item => item.className);

        list.sort((a, b) => b.probability - a.probability);
        this.predictions = list;

        const best = list[0];

        if (!best) {
            this.rawLabel = '';
            this.rawConfidence = 0;
            this.bestLabel = '';
            this.bestConfidence = 0;
            this.status = 'No prediction';
            return;
        }

        this.rawLabel = best.className;
        this.rawConfidence = best.probability * 100;

        if (!options.smooth) {
            this.bestLabel = best.className;
            this.bestConfidence = best.probability * 100;
            this.status = `${options.statusPrefix || 'Detected'} ${this.bestLabel}`;
            this._startHats();
            return;
        }

        const stable = this._getStablePrediction(list);

        if (stable && stable.accepted) {
            this.bestLabel = stable.className;
            this.bestConfidence = stable.probability * 100;
            this.status = `${options.statusPrefix || 'Detected'} ${this.bestLabel}`;
            this._startHats();
            return;
        }

        if (this.bestLabel) {
            this.status = `Seeing ${this.rawLabel}, keeping ${this.bestLabel}`;
        } else {
            this.status = `Seeing ${this.rawLabel}, waiting for stable result`;
        }
    }

    _getStablePrediction(sortedList) {
        const best = sortedList[0];
        const second = sortedList[1];
        const margin = best.probability - (second ? second.probability : 0);

        if (best.probability < this.minStableProbability) {
            return null;
        }

        this.predictionHistory.push({
            className: best.className,
            probability: best.probability,
            margin
        });

        while (this.predictionHistory.length > this.historySize) {
            this.predictionHistory.shift();
        }

        const grouped = {};

        this.predictionHistory.forEach(item => {
            const key = item.className;

            if (!grouped[key]) {
                grouped[key] = {
                    className: key,
                    count: 0,
                    probabilitySum: 0,
                    maxMargin: 0
                };
            }

            grouped[key].count++;
            grouped[key].probabilitySum += item.probability;
            grouped[key].maxMargin = Math.max(grouped[key].maxMargin, item.margin);
        });

        const groups = Object.values(grouped).sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return b.probabilitySum - a.probabilitySum;
        });

        const winner = groups[0];

        if (!winner) return null;

        const avgProbability = winner.probabilitySum / winner.count;

        if (!this.bestLabel) {
            if (
                winner.count >= this.requiredInitialFrames ||
                (best.probability >= this.strongSwitchProbability && margin >= this.strongSwitchMargin)
            ) {
                return {
                    accepted: true,
                    className: winner.className,
                    probability: avgProbability
                };
            }

            return null;
        }

        if (this._sameLabel(winner.className, this.bestLabel)) {
            return {
                accepted: true,
                className: winner.className,
                probability: avgProbability
            };
        }

        if (
            winner.count >= this.requiredStableFrames &&
            avgProbability >= this.minStableProbability &&
            winner.maxMargin >= this.minSwitchMargin
        ) {
            return {
                accepted: true,
                className: winner.className,
                probability: avgProbability
            };
        }

        if (
            best.probability >= this.strongSwitchProbability &&
            margin >= this.strongSwitchMargin &&
            winner.count >= 2
        ) {
            return {
                accepted: true,
                className: winner.className,
                probability: avgProbability
            };
        }

        return null;
    }

    _startHats() {
        if (this.runtime && typeof this.runtime.startHats === 'function') {
            this.runtime.startHats('madbotTrainableAI_whenClassDetected');
        }
    }

    _resetStabilizer() {
        this.predictionHistory = [];
        this.rawLabel = '';
        this.rawConfidence = 0;
    }

    _clearPredictionState() {
        this.predictions = [];
        this.rawLabel = '';
        this.rawConfidence = 0;
        this.bestLabel = '';
        this.bestConfidence = 0;
        this.predictedNumberValue = 0;
        this._resetStabilizer();
    }

    _pickJsonFile() {
        return new Promise((resolve, reject) => {
            if (typeof document === 'undefined') {
                reject(new Error('File picker works only in browser'));
                return;
            }

            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json,application/json';
            input.style.position = 'fixed';
            input.style.left = '-9999px';
            input.style.top = '-9999px';

            input.addEventListener('change', async () => {
                try {
                    const file = input.files && input.files[0];

                    if (!file) {
                        reject(new Error('No file selected'));
                        return;
                    }

                    const text = await file.text();
                    const json = JSON.parse(text);

                    resolve(json);
                } catch (error) {
                    reject(new Error('Invalid JSON model file'));
                } finally {
                    input.remove();
                }
            });

            document.body.appendChild(input);
            input.click();
        });
    }

    _loadMadBotMLLabFile(data) {
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid MadBot ML Lab file');
        }

        this.model = null;
        this.modelBaseURL = '';
        this._clearMadBotModels();
        this._clearPredictionState();

        this.projectName = data.projectName || data.name || 'MadBot ML Lab Project';

        if (data.image || data.draw || data.text || data.number) {
            this._loadFullProjectFormat(data);
        } else if (data.madbotModel || data.source === 'madbotMLLab' || data.modelType) {
            this._loadFlatModelFormat(data);
        } else {
            throw new Error('This is not a MadBot ML Lab model file');
        }

        if (!this._hasAnyMadBotModel()) {
            throw new Error('No usable model data found in this file');
        }

        this.modelSource = 'madbotMLLab';
        this.labels = this._collectMadBotLabels();

        if (this.madbotModels.draw) {
            this.activeModelType = 'draw';
        } else if (this.madbotModels.image) {
            this.activeModelType = 'image';
        } else if (this.madbotModels.text) {
            this.activeModelType = 'text';
        } else if (this.madbotModels.number) {
            this.activeModelType = 'number';
        }
    }

    _loadFullProjectFormat(project) {
        if (project.image && Array.isArray(project.image.classes)) {
            const model = this._makeVisualModel('image', project.image.classes, 32);
            if (model) this.madbotModels.image = model;
        }

        if (project.draw && Array.isArray(project.draw.classes)) {
            const model = this._makeVisualModel('draw', project.draw.classes, 28);
            if (model) this.madbotModels.draw = model;
        }

        if (project.text && Array.isArray(project.text.classes)) {
            const textClasses = this._normalizeTextClasses(project.text.classes);

            if (this._hasTextExamples(textClasses)) {
                this.madbotModels.text = {
                    type: 'text',
                    classes: textClasses,
                    model: this._buildNaiveBayesModel(textClasses)
                };
            }
        }

        if (project.number) {
            const numberModel = this._makeNumberModel(project.number);
            if (numberModel) this.madbotModels.number = numberModel;
        }
    }

    _loadFlatModelFormat(data) {
        const type = String(data.modelType || data.type || '').toLowerCase();

        if (type === 'image' || type === 'draw') {
            const classData = this._classesFromFlatVisualModel(data);
            const detectedInputSize = this._detectVectorInputSize(classData, type);
            const inputSize = Number(data.settings && data.settings.inputSize) ||
                detectedInputSize ||
                (type === 'draw' ? 28 : 32);

            const model = this._makeVisualModel(type, classData, inputSize);

            if (model) this.madbotModels[type] = model;
            return;
        }

        if (type === 'text') {
            const textClasses = this._classesFromFlatTextModel(data);

            if (this._hasTextExamples(textClasses)) {
                this.madbotModels.text = {
                    type: 'text',
                    classes: textClasses,
                    model: data.model && data.model.classStats ? data.model : this._buildNaiveBayesModel(textClasses)
                };
            }

            return;
        }

        if (type === 'number') {
            const numberData = {
                rows: data.rows || data.samples || data.data || [],
                model: data.model
            };

            const numberModel = this._makeNumberModel(numberData);
            if (numberModel) this.madbotModels.number = numberModel;
        }
    }

    _makeVisualModel(type, rawClasses, defaultInputSize) {
        const classes = (rawClasses || []).map((cls, index) => {
            const name = cls.name || cls.label || `Class ${index + 1}`;

            const examples = (cls.examples || cls.samples || [])
                .map(example => {
                    if (Array.isArray(example)) return { vector: example };
                    if (Array.isArray(example.vector)) return { vector: example.vector };
                    if (Array.isArray(example.features)) return { vector: example.features };
                    if (Array.isArray(example.input)) return { vector: example.input };
                    return null;
                })
                .filter(Boolean);

            return {
                name,
                examples
            };
        });

        const totalExamples = classes.reduce((sum, cls) => sum + cls.examples.length, 0);

        if (classes.length < 2 || totalExamples < 2) {
            return null;
        }

        const detectedInputSize = this._detectVectorInputSize(classes, type);

        return {
            type,
            algorithm: 'knn',
            inputSize: detectedInputSize || defaultInputSize,
            classes,
            // Important:
            // Draw AI works better with k = 1.
            // Image AI works better with k = 5.
            k: type === 'draw' ? 1 : 5
        };
    }

    _classesFromFlatVisualModel(data) {
        const classNames = this._readClassNames(data.classes);
        const classes = classNames.map(name => ({
            name,
            examples: []
        }));

        const samples = Array.isArray(data.samples) ? data.samples : [];

        samples.forEach(sample => {
            const label = sample.label || sample.className || sample.name || sample.class;

            const vector = Array.isArray(sample.vector) ? sample.vector :
                Array.isArray(sample.features) ? sample.features :
                    Array.isArray(sample.input) ? sample.input :
                    null;

            if (!label || !vector) return;

            let cls = classes.find(item => item.name === label);

            if (!cls) {
                cls = {
                    name: label,
                    examples: []
                };
                classes.push(cls);
            }

            cls.examples.push({
                vector
            });
        });

        return classes;
    }

    _classesFromFlatTextModel(data) {
        if (Array.isArray(data.classes) && data.classes.some(item => typeof item === 'object')) {
            return this._normalizeTextClasses(data.classes);
        }

        const classNames = this._readClassNames(data.classes);
        const classes = classNames.map(name => ({
            name,
            examples: []
        }));

        const samples = Array.isArray(data.samples) ? data.samples : [];

        samples.forEach(sample => {
            const label = sample.label || sample.className || sample.name || sample.class;
            const text = sample.text || sample.input || sample.value || sample.example;

            if (!label || !text) return;

            let cls = classes.find(item => item.name === label);

            if (!cls) {
                cls = {
                    name: label,
                    examples: []
                };
                classes.push(cls);
            }

            cls.examples.push(String(text));
        });

        return classes;
    }

    _readClassNames(classesField) {
        if (!Array.isArray(classesField)) return [];

        return classesField.map((item, index) => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object') return item.name || item.label || `Class ${index + 1}`;
            return `Class ${index + 1}`;
        });
    }

    _normalizeTextClasses(rawClasses) {
        return (rawClasses || []).map((cls, index) => {
            const name = cls.name || cls.label || `Class ${index + 1}`;

            const examples = (cls.examples || cls.samples || [])
                .map(example => {
                    if (typeof example === 'string') return example;

                    if (example && typeof example === 'object') {
                        return example.text || example.input || example.value || example.example || '';
                    }

                    return '';
                })
                .map(text => String(text).trim())
                .filter(Boolean);

            return {
                name,
                examples
            };
        });
    }

    _hasTextExamples(classes) {
        return classes &&
            classes.length >= 2 &&
            classes.every(cls => cls.examples && cls.examples.length > 0);
    }

    _makeNumberModel(numberData) {
        if (
            numberData.model &&
            Number.isFinite(Number(numberData.model.m)) &&
            Number.isFinite(Number(numberData.model.b))
        ) {
            return {
                type: 'number',
                model: {
                    m: Number(numberData.model.m),
                    b: Number(numberData.model.b)
                }
            };
        }

        const rows = Array.isArray(numberData.rows) ? numberData.rows : [];

        const points = rows.map(row => {
            if (Array.isArray(row)) {
                return {
                    x: Number(row[0]),
                    y: Number(row[1])
                };
            }

            return {
                x: Number(row.x !== undefined ? row.x : row.input),
                y: Number(row.y !== undefined ? row.y : row.output)
            };
        }).filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));

        if (points.length < 2) return null;

        return {
            type: 'number',
            model: this._linearRegression(points),
            points
        };
    }

    _buildNaiveBayesModel(classes) {
        const vocab = new Set();

        const classStats = classes.map(cls => {
            const counts = {};
            let tokenCount = 0;

            (cls.examples || []).forEach(example => {
                this._tokenize(example).forEach(token => {
                    vocab.add(token);
                    counts[token] = (counts[token] || 0) + 1;
                    tokenCount++;
                });
            });

            return {
                label: cls.name,
                counts,
                tokenCount,
                docCount: (cls.examples || []).length
            };
        });

        const totalDocs = classes.reduce((sum, cls) => sum + ((cls.examples || []).length), 0);

        return {
            vocab: Array.from(vocab),
            classStats,
            totalDocs
        };
    }

    _predictNaiveBayes(text, model) {
        const tokens = this._tokenize(text);
        const vocabSize = Math.max(model.vocab ? model.vocab.length : 0, 1);
        const classStats = model.classStats || [];
        const totalDocs = model.totalDocs ||
            classStats.reduce((sum, cls) => sum + (cls.docCount || 0), 0) ||
            1;

        const rawScores = classStats.map(cls => {
            let score = Math.log(((cls.docCount || 0) + 1) / (totalDocs + classStats.length));

            tokens.forEach(token => {
                const count = cls.counts && cls.counts[token] ? cls.counts[token] : 0;
                score += Math.log((count + 1) / ((cls.tokenCount || 0) + vocabSize));
            });

            return {
                label: cls.label,
                score
            };
        });

        if (!rawScores.length) {
            return {
                label: '',
                confidence: 0,
                probabilities: []
            };
        }

        const maxScore = Math.max(...rawScores.map(item => item.score));

        const expScores = rawScores.map(item => ({
            label: item.label,
            value: Math.exp(item.score - maxScore)
        }));

        const sum = expScores.reduce((a, b) => a + b.value, 0) || 1;

        const probabilities = expScores.map(item => ({
            className: item.label,
            probability: item.value / sum
        })).sort((a, b) => b.probability - a.probability);

        return {
            label: probabilities[0] ? probabilities[0].className : '',
            confidence: probabilities[0] ? probabilities[0].probability : 0,
            probabilities
        };
    }

    _predictKNN(vector, classes, k = 5) {
        const distances = [];

        classes.forEach((cls, classIndex) => {
            (cls.examples || []).forEach(example => {
                if (!example || !Array.isArray(example.vector)) return;

                distances.push({
                    classIndex,
                    className: cls.name,
                    distance: this._squaredDistance(vector, example.vector)
                });
            });
        });

        if (!distances.length) {
            return {
                label: '',
                confidence: 0,
                probabilities: []
            };
        }

        distances.sort((a, b) => a.distance - b.distance);

        const nearest = distances.slice(0, Math.max(1, Math.min(k, distances.length)));
        const scores = {};

        nearest.forEach((item, rank) => {
            const weight = 1 / (Math.sqrt(item.distance) + 1e-6 + rank * 0.002);
            scores[item.classIndex] = (scores[item.classIndex] || 0) + weight;
        });

        const total = Object.values(scores).reduce((a, b) => a + b, 0) || 1;

        const probabilities = classes.map((cls, index) => ({
            className: cls.name,
            probability: (scores[index] || 0) / total
        })).sort((a, b) => b.probability - a.probability);

        return {
            label: probabilities[0] ? probabilities[0].className : '',
            confidence: probabilities[0] ? probabilities[0].probability : 0,
            probabilities
        };
    }

    _squaredDistance(a, b) {
        const len = Math.min(a.length, b.length);

        if (!len) return Number.POSITIVE_INFINITY;

        let sum = 0;

        for (let i = 0; i < len; i++) {
            const d = Number(a[i]) - Number(b[i]);
            sum += d * d;
        }

        return sum / len;
    }

    _imageCanvasToVector(sourceCanvas, size = 32) {
        if (!sourceCanvas) return null;

        if (!this.imageInputCanvas) {
            this.imageInputCanvas = document.createElement('canvas');
        }

        this.imageInputCanvas.width = size;
        this.imageInputCanvas.height = size;

        const ctx = this.imageInputCanvas.getContext('2d', {
            willReadFrequently: true
        });

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);

        const sw = sourceCanvas.width;
        const sh = sourceCanvas.height;
        const crop = Math.min(sw, sh);
        const sx = Math.floor((sw - crop) / 2);
        const sy = Math.floor((sh - crop) / 2);

        ctx.drawImage(sourceCanvas, sx, sy, crop, crop, 0, 0, size, size);

        const data = ctx.getImageData(0, 0, size, size).data;
        const vector = [];

        for (let i = 0; i < data.length; i += 4) {
            vector.push(data[i] / 255, data[i + 1] / 255, data[i + 2] / 255);
        }

        return vector;
    }

    _drawCanvasToVector(sourceCanvas, size = 28) {
        if (!sourceCanvas) return null;

        const sourceWidth = sourceCanvas.width || this.cameraWidth;
        const sourceHeight = sourceCanvas.height || this.cameraHeight;

        const src = sourceCanvas.getContext('2d', {
            willReadFrequently: true
        });

        const imageData = src.getImageData(0, 0, sourceWidth, sourceHeight);
        const box = this._findInkBoundingBox(imageData, sourceWidth, sourceHeight);

        if (!box) return null;

        if (!this.drawInputCanvas) {
            this.drawInputCanvas = document.createElement('canvas');
        }

        this.drawInputCanvas.width = size;
        this.drawInputCanvas.height = size;

        const ctx = this.drawInputCanvas.getContext('2d', {
            willReadFrequently: true
        });

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);

        const margin = 10;
        const boxWidth = box.maxX - box.minX + 1;
        const boxHeight = box.maxY - box.minY + 1;

        const scale = Math.min((size - margin) / boxWidth, (size - margin) / boxHeight);
        const drawWidth = boxWidth * scale;
        const drawHeight = boxHeight * scale;
        const drawX = (size - drawWidth) / 2;
        const drawY = (size - drawHeight) / 2;

        ctx.drawImage(
            sourceCanvas,
            box.minX,
            box.minY,
            boxWidth,
            boxHeight,
            drawX,
            drawY,
            drawWidth,
            drawHeight
        );

        const data = ctx.getImageData(0, 0, size, size).data;
        const vector = [];

        for (let i = 0; i < data.length; i += 4) {
            const gray = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
            vector.push(1 - gray);
        }

        return vector;
    }

    _findInkBoundingBox(imageData, width, height) {
        let minX = width;
        let minY = height;
        let maxX = -1;
        let maxY = -1;
        let inkPixels = 0;

        const data = imageData.data;

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4;

                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const a = data[i + 3];

                if (a <= 10) continue;

                if (r < 245 || g < 245 || b < 245) {
                    inkPixels++;

                    if (x < minX) minX = x;
                    if (y < minY) minY = y;
                    if (x > maxX) maxX = x;
                    if (y > maxY) maxY = y;
                }
            }
        }

        if (maxX === -1 || inkPixels < 10) return null;

        return {
            minX,
            minY,
            maxX,
            maxY,
            inkPixels
        };
    }

    _detectVectorInputSize(classes, type) {
        for (const cls of classes || []) {
            for (const example of cls.examples || []) {
                if (example && Array.isArray(example.vector) && example.vector.length > 0) {
                    const length = example.vector.length;

                    if (type === 'image') {
                        const imageSize = Math.sqrt(length / 3);
                        if (Number.isInteger(imageSize)) return imageSize;
                    }

                    const drawSize = Math.sqrt(length);
                    if (Number.isInteger(drawSize)) return drawSize;
                }
            }
        }

        return 0;
    }

    _tokenize(text) {
        return String(text)
            .toLowerCase()
            .replace(/[^\p{L}\p{N}\s]/gu, ' ')
            .split(/\s+/)
            .map(token => token.trim())
            .filter(Boolean);
    }

    _linearRegression(points) {
        const n = points.length;
        const sumX = points.reduce((sum, point) => sum + point.x, 0);
        const sumY = points.reduce((sum, point) => sum + point.y, 0);
        const sumXY = points.reduce((sum, point) => sum + point.x * point.y, 0);
        const sumXX = points.reduce((sum, point) => sum + point.x * point.x, 0);
        const denom = n * sumXX - sumX * sumX;

        const m = Math.abs(denom) < 1e-12 ? 0 : (n * sumXY - sumX * sumY) / denom;
        const b = (sumY - m * sumX) / n;

        return {
            m,
            b
        };
    }

    _hasVisualModel() {
        if (this.modelSource === 'teachableMachine' && this.model) return true;

        if (
            this.modelSource === 'madbotMLLab' &&
            (this.madbotModels.image || this.madbotModels.draw)
        ) {
            return true;
        }

        return false;
    }

    _hasAnyMadBotModel() {
        return Boolean(
            this.madbotModels.image ||
            this.madbotModels.draw ||
            this.madbotModels.text ||
            this.madbotModels.number
        );
    }

    _clearMadBotModels() {
        this.madbotModels = {
            image: null,
            draw: null,
            text: null,
            number: null
        };
    }

    _collectMadBotLabels() {
        const labels = [];

        const add = label => {
            if (label && !labels.some(item => this._sameLabel(item, label))) {
                labels.push(label);
            }
        };

        if (this.madbotModels.draw && this.madbotModels.draw.classes) {
            this.madbotModels.draw.classes.forEach(cls => add(cls.name));
        }

        if (this.madbotModels.image && this.madbotModels.image.classes) {
            this.madbotModels.image.classes.forEach(cls => add(cls.name));
        }

        if (this.madbotModels.text && this.madbotModels.text.classes) {
            this.madbotModels.text.classes.forEach(cls => add(cls.name));
        }

        return labels;
    }

    _chooseMadBotVisualModelType() {
        // For alphabet/drawing recognition, stage and backdrop should prefer Draw AI.
        if ((this.source === 'stage' || this.source === 'backdrop') && this.madbotModels.draw) {
            return 'draw';
        }

        // Camera usually prefers image model.
        if (this.source === 'camera' && this.madbotModels.image) {
            return 'image';
        }

        if (this.madbotModels.draw) {
            return 'draw';
        }

        if (this.madbotModels.image) {
            return 'image';
        }

        return '';
    }

    _arrayBufferToBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const length = bytes.byteLength;

        for (let i = 0; i < length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        return window.btoa(binary);
    }

    _removeOldDebugPreview() {
        if (typeof document === 'undefined') return;

        const previews = document.querySelectorAll('div');

        previews.forEach(div => {
            if (div.innerText && div.innerText.includes('AI sees this')) {
                div.remove();
            }
        });
    }

    _sameLabel(a, b) {
        return Cast.toString(a).trim().toLowerCase() === Cast.toString(b).trim().toLowerCase();
    }

    _round1(value) {
        return Math.round(Cast.toNumber(value) * 10) / 10;
    }

    _round(value, digits = 2) {
        const num = Number(value) || 0;
        return Number(num.toFixed(digits));
    }
}

module.exports = Scratch3MadBotTrainableAI;